import { useEffect, useCallback, useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { PlaygroundType } from "@/services/api/widget/types";
import type { ExtendedAssetInfo } from "./useAssetManager";
import type { ChartSettings } from "./usePlaygroundSettings";
import {
  parsePlaygroundAssetsParam,
  parsePlaygroundChartSettingsFromSearchParams,
  parsePlaygroundTypeFromSearchParams,
} from "@/navigation/apps/playgroundLocation";

interface UrlParamConfig {
  onUrlParamsLoaded?: (params: Partial<UrlParameters>) => void;
}

interface UrlParameters {
  playgroundType: PlaygroundType;
  assets: string[];
  assetDisplayNames: Record<string, string>;
  assetMetadata: Record<string, ExtendedAssetInfo>;
  chartSettings: Partial<ChartSettings>;
}

export const useUrlParameters = ({
  onUrlParamsLoaded,
}: UrlParamConfig = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAssetsParamRef = useRef<string | null>(null);

  // Load URL parameters on mount or when assets param changes (navigation)
  useEffect(() => {
    const currentAssetsParam = searchParams.get("assets");

    // Check if we have new assets in URL (navigation from Screener)
    const hasNewAssets =
      currentAssetsParam && currentAssetsParam !== lastAssetsParamRef.current;

    // Skip if already loaded and no new assets
    if (isLoaded && !hasNewAssets) return;

    // Update the ref to track current assets param
    lastAssetsParamRef.current = currentAssetsParam;

    try {
      const params: Partial<UrlParameters> = {};

      const playgroundType = parsePlaygroundTypeFromSearchParams(searchParams);
      if (playgroundType) {
        params.playgroundType = playgroundType;
      }

      const chartSettings = parsePlaygroundChartSettingsFromSearchParams(searchParams);
      if (Object.keys(chartSettings).length > 0) {
        params.chartSettings = chartSettings;
      }

      const assetsParam = searchParams.get("assets");
      if (assetsParam) {
        const parsed = parsePlaygroundAssetsParam(assetsParam);
        if (parsed.initialAssets.length > 0) {
          params.assets = parsed.initialAssets;
          if (Object.keys(parsed.initialDisplayNames).length > 0) {
            params.assetDisplayNames = parsed.initialDisplayNames;
          }
          params.assetMetadata = parsed.initialAssetMetadata;
        }
      }

      if (onUrlParamsLoaded) {
        onUrlParamsLoaded(params);
      }
    } catch (err) {
      console.error("[useUrlParameters] Error loading URL parameters:", err);
    } finally {
      setIsLoaded(true);
    }
  }, [searchParams, isLoaded, onUrlParamsLoaded]);

  // Update URL parameters (debounced)
  const updateUrl = useCallback(
    (params: {
      playgroundType?: PlaygroundType;
      assets?: string[];
      assetMetadata?: Record<string, ExtendedAssetInfo>;
      chartSettings?: Partial<ChartSettings>;
    }) => {
      if (!isLoaded) return;

      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Set a new timeout to update the URL
      updateTimeoutRef.current = setTimeout(() => {
        const urlParams = new URLSearchParams();

        // Add playground type
        if (params.playgroundType) {
          urlParams.set("type", params.playgroundType);
        }

        // Add selected assets with their metadata
        if (params.assets && params.assets.length > 0 && params.assetMetadata) {
          const assetsParam = params.assets
            .map((assetId) => {
              const meta = params.assetMetadata?.[assetId];
              if (meta) {
                return `${meta.displayName}:::${meta.resourceId}:::${meta.resourceType}:::${meta.streamType}`;
              }
              return `:::${assetId}:::product:::total_return`;
            })
            .join(",");
          urlParams.set("assets", assetsParam);
        }

        // Add chart settings
        if (params.chartSettings) {
          const {
            period,
            displayMode,
            rebaseValue,
            frequency,
            statistic,
            windowSize,
            customDateRange,
            exposureDisplayMode,
            extendIncompleteWithProxy,
          } = params.chartSettings;

          if (period) urlParams.set("period", period);
          if (displayMode) urlParams.set("display", displayMode);
          if (rebaseValue) urlParams.set("rebase", rebaseValue.toString());
          if (frequency) urlParams.set("frequency", frequency);
          if (statistic) urlParams.set("statistic", statistic);
          if (windowSize) urlParams.set("window", windowSize.toString());
          if (exposureDisplayMode) {
            urlParams.set("exposure_display", exposureDisplayMode);
          }
          if (extendIncompleteWithProxy) {
            urlParams.set("proxy_extend", "1");
          }

          // Add custom date range if period is custom
          if (
            period === "custom" &&
            customDateRange?.[0] &&
            customDateRange?.[1]
          ) {
            urlParams.set("start_date", customDateRange[0]);
            urlParams.set("end_date", customDateRange[1]);
          }
        }

        // Update URL without reloading the page
        setSearchParams(urlParams, { replace: true });
      }, 300); // 300ms debounce
    },
    [isLoaded, setSearchParams]
  );

  return {
    isLoaded,
    updateUrl,
  };
};
